import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandsPraying,
  faSun,
  faCalendarDays,
  faSeedling,
  faClock,
} from "@fortawesome/free-solid-svg-icons";

interface WorshipItem {
  name: string;
  time: string;
  place: string;
}

interface WorshipCategory {
  id: string;
  icon: typeof faSun;
  title: string;
  items: WorshipItem[];
}

const worshipData: WorshipCategory[] = [
  {
    id: "sunday",
    icon: faSun,
    title: "주일 (Sunday)",
    items: [
      {
        name: "주일낮예배",
        time: "오전 11:00",
        place: "- 4층 예배당",
      },
      {
        name: "하가모 (하늘가족모임)",
        time: "주일예배 후",
        place: "- 3~4층 예배당",
      },
    ],
  },
  {
    id: "weekdays",
    icon: faCalendarDays,
    title: "주중 (Weekdays)",
    items: [
      {
        name: "화요성경강좌",
        time: "화요일 오전 10:00",
        place: "- 온/오프라인 병행",
      },
      {
        name: "금요비상기도모임",
        time: "금요일 저녁 8:00",
        place: "- 4층 예배당",
      },
    ],
  },
  {
    id: "nextgen",
    icon: faSeedling,
    title: "다음세대 (Next Gen)",
    items: [
      {
        name: "세대통합예배",
        time: "주일 오전 11:00",
        place: "- 4층 예배당 (부모님과 함께)",
      },
    ],
  },
];

const WorshipSection = () => {
  return (
    <Section className="worship-section" id="worship">
      <Container className="container">
        <SectionHeader className="section-header">
          <SectionTitle className="section-title">
            <FontAwesomeIcon icon={faHandsPraying} className="section-icon" />{" "}
            처음사랑교회 예배와모임 안내
          </SectionTitle>
          <SectionDesc className="section-desc">
            성경의 진리가 선포되고, 진리를 결론 삼는 시간입니다.
          </SectionDesc>
        </SectionHeader>

        <WorshipGrid className="worship-grid">
          {worshipData.map((category) => (
            <WorshipCard key={category.id} className="worship-card">
              <WorshipHeader className="worship-header">
                <FontAwesomeIcon
                  icon={category.icon}
                  className="worship-icon"
                />
                <WorshipCate className="worship-cate">
                  {category.title}
                </WorshipCate>
              </WorshipHeader>
              <WorshipList className="worship-list">
                {category.items.map((item, index) => (
                  <WorshipItem key={index} className="worship-item">
                    <WorshipName className="worship-name">
                      {item.name}
                    </WorshipName>
                    <WorshipTime className="worship-time">
                      <FontAwesomeIcon icon={faClock} /> {item.time}
                    </WorshipTime>
                    <WorshipPlace className="worship-place">
                      {item.place}
                    </WorshipPlace>
                  </WorshipItem>
                ))}
              </WorshipList>
            </WorshipCard>
          ))}
        </WorshipGrid>
      </Container>
    </Section>
  );
};

const Section = styled.section`
  background: white;
  padding: 80px 0;
  position: relative;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 15px;
  }

  @media (max-width: 480px) {
    padding: 0 10px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 50px;
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  color: #004094;
  margin-bottom: 5px;
  letter-spacing: -1px;
  line-height: 1.2;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-weight: 700;

  .section-icon {
    font-size: 0.9em;
    color: #004094;
  }

  @media (max-width: 768px) {
    font-size: 24px;
    flex-direction: column;
    text-align: center;
  }

  @media (max-width: 480px) {
    font-size: 20px;
    gap: 5px;
  }
`;

const SectionDesc = styled.p`
  font-size: 18px;
  color: #555;
  font-weight: 300;
  word-break: keep-all;
`;

const WorshipGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const WorshipCard = styled.div`
  background: white;
  border: 1px solid #eee;
  border-radius: 10px;
  padding: 35px 25px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.03);
  transition: 0.3s;

  &:hover {
    transform: translateY(-5px);
    border-color: #004094;
    box-shadow: 0 10px 25px rgba(0, 64, 148, 0.08);
  }

  @media (max-width: 768px) {
    padding: 25px 20px;
  }

  @media (max-width: 480px) {
    padding: 20px 15px;
  }
`;

const WorshipHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 25px;
  border-bottom: 2px solid #004094;
  padding-bottom: 10px;

  .worship-icon {
    font-size: 20px;
    color: #004094;
  }
`;

const WorshipCate = styled.span`
  font-size: 20px;
  color: #004094;
  font-weight: 700;
`;

const WorshipList = styled.div`
  margin-top: 10px;
`;

const WorshipItem = styled.div`
  margin-bottom: 20px;
`;

const WorshipName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #222;
  margin-bottom: 5px;
`;

const WorshipTime = styled.div`
  font-size: 15px;
  color: #666;
  font-weight: 300;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const WorshipPlace = styled.div`
  font-size: 14px;
  color: #888;
  font-weight: 300;
  margin-left: 20px;
`;

export default WorshipSection;
